import {
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  VStack,
} from '@chakra-ui/react';
import { CancelIcon, CommentIcon, FilterIcon, RemoveIcon } from 'Tokens/Icons/Function';
import { useState, useMemo, useEffect } from 'react';
import {
  AggregatedMetricsTableData,
  MetricsTableData,
  isFullMetricOnBULevel,
  areArraysOfObjectsEqual,
} from '..';
import { TimePeriodsEnums, FrequencyEnums } from '../../Requirement';
import { GeneratedAnswerStatus, GeneratedAnswer } from '../MetricAI';
import { AssessableMetrics } from '../Metrics';
import { useGetDatapointValues } from './MetricsTableComponents/InputFields/QuantitativeInputs';
import { Button, Checkbox, IconButton } from 'Atoms';
import { useNotifications } from 'Features';
import { Typography } from 'Tokens';
import { SearchInput, Select } from 'Molecules';
import { Row } from '@tanstack/react-table';
import { TableData } from 'Molecules/NestedTable';
import { MetricTypes } from 'Molecules/MetricTypeIcon';
import { DataCollectionLevel } from '../../../DataCollection';
import { getRowName } from './MetricsTableComponents/MetricRowTitle/MetricRowTitle.hooks';
import { useParams } from 'react-router-dom';
import { ChevronRightIcon, CheckIcon } from '@chakra-ui/icons';
import { TeamMember, TeamMembersFilterOption, useTeamMembers } from 'containers/TeamMembers';
import { GroupOrderOptions } from '../AggregatedMetrics/AggregatedMetricsUtils';

export type DataPointIcon = {
  type: MetricTypes;
  calculation: string | null | undefined;
  subMetrics?: {
    name: string;
    reference: string;
  }[];
  isBusinessUnits: boolean;
  isSubsidiaries: boolean;
  isQuarterly: boolean;
};

const calculateType = ({
  row,
  showInput,
}: {
  row?: Row<TableData<MetricsTableData>>;
  showInput?: boolean;
}) => {
  if (row?.subRows.length !== 0) {
    return MetricTypes.calculated;
  }
  if (showInput) {
    return MetricTypes.edit;
  }
  return MetricTypes.internallyAggregated;
};

export const AnswerApprovalButton = ({
  state,
  onApprove,
  onDiscard,
  onCancelApprove,
  onCancelDiscard,
}: {
  onApprove: () => void;
  onDiscard: () => void;
  onCancelApprove: () => void;
  onCancelDiscard: () => void;
  state: GeneratedAnswerStatus | null;
}) => {
  if (state === GeneratedAnswerStatus.approved) {
    return (
      <HStack spacing="2px">
        <Button selected variant="secondary" size="sm">
          Approved
        </Button>
        <IconButton
          tooltipLabel="Cancel"
          aria-label="Cancel"
          size="sm"
          variant="ghost"
          icon={<CancelIcon />}
          onClick={onCancelApprove}
        />
      </HStack>
    );
  }

  if (state === GeneratedAnswerStatus.discarded) {
    return (
      <HStack spacing="2px">
        <Button selected variant="secondary" size="sm">
          Discarded
        </Button>{' '}
        <IconButton
          tooltipLabel="Cancel"
          aria-label="Cancel"
          size="sm"
          variant="ghost"
          icon={<CancelIcon />}
          onClick={onCancelDiscard}
        />
      </HStack>
    );
  }

  return (
    <HStack spacing="2px">
      <Button variant="secondary" size="sm" onClick={onApprove}>
        Approve
      </Button>
      <IconButton
        tooltipLabel="Discard"
        aria-label="Discard"
        size="sm"
        variant="ghost"
        icon={<RemoveIcon />}
        onClick={onDiscard}
      />
    </HStack>
  );
};

export const AnswerApprovalCell = ({
  row,
  companyReportingUnitId,
  generatedAnswers,
  setGeneratedAnswers,
  populateQuantitativeAnswers,
  populateNarrativeAnswers,
}: {
  row: MetricsTableData;
  companyReportingUnitId?: string;
  generatedAnswers: GeneratedAnswer[];
  setGeneratedAnswers?: (generatedAnswers: GeneratedAnswer[]) => void;
  populateQuantitativeAnswers?: (
    generatedAnswers: GeneratedAnswer[],
    numericMetrics: MetricsTableData[]
  ) => Promise<void>;
  populateNarrativeAnswers?: (
    generatedAnswers: GeneratedAnswer[],
    narrativeMetrics: AssessableMetrics
  ) => Promise<void>;
}) => {
  const [prevValue, setPrevValue] = useState<string | null>();

  const { dataPointPerYear, answer, onDatapointChange } = useGetDatapointValues(
    row,
    companyReportingUnitId
  );

  const generatedAnswer = useMemo(
    () =>
      generatedAnswers?.find(
        (a) => a.metricRef === row?.metric?.reference && areArraysOfObjectsEqual(a.tags, row?.tags)
      ),
    [generatedAnswers, row]
  );

  useEffect(() => {
    if (dataPointPerYear) {
      setPrevValue(dataPointPerYear.value);
    }
  }, []);

  const handleChangeStatus = (status: GeneratedAnswerStatus | null) => {
    if (setGeneratedAnswers) {
      setGeneratedAnswers(
        (generatedAnswers ?? []).map((a) => {
          if (a.metricRef === row.metric.reference && areArraysOfObjectsEqual(a.tags, row?.tags)) {
            return {
              ...a,
              status,
            };
          }
          return a;
        })
      );
    }
  };

  return (
    generatedAnswer && (
      <AnswerApprovalButton
        state={generatedAnswer?.status}
        onApprove={async () => {
          if (populateQuantitativeAnswers)
            await populateQuantitativeAnswers([generatedAnswer], [row]);
          if (populateNarrativeAnswers)
            await populateNarrativeAnswers([generatedAnswer], [row.metric]);
          handleChangeStatus(GeneratedAnswerStatus.approved);
        }}
        onDiscard={() => handleChangeStatus(GeneratedAnswerStatus.discarded)}
        onCancelApprove={async () => {
          handleChangeStatus(null);
          if (prevValue) {
            await onDatapointChange({
              dp: dataPointPerYear ?? { timeframe: TimePeriodsEnums.year },
              value: prevValue ?? null,
              hasOptedOut: answer?.hasOptedOut ?? false,
              optOutReason: answer?.optOutReason ?? '',
              tags: row.tags,
            });
          }
        }}
        onCancelDiscard={() => handleChangeStatus(null)}
      />
    )
  );
};

export enum TaskFilterOptions {
  allTasks = 'All tasks',
  yourTasks = 'Your tasks',
  members = 'Members tasks',
  unassigned = 'Unassigned tasks',
  additionalInformation = 'Additional information',
  markedAsIrrelevant = 'Marked as irrelevant',
  materiality = 'Materiality',
}

export enum AdditionalInformationOptions {
  voluntary = 'Voluntary',
  conditional = 'Conditional',
  basic = 'VSME Basic',
  comprehensive = 'VSME Comprehensive',
}

const AdditionalTypesMenu = ({
  additionalTypesOptions,
  selectedAdditionalTypes,
  setSelectedAdditionalTypes,
}: {
  additionalTypesOptions: {
    label: string;
    ref: string;
  }[];
  selectedAdditionalTypes?: string[];
  setSelectedAdditionalTypes?: (val: string[]) => void;
}) => {
  const defaultTypes: string[] = Object.values(AdditionalInformationOptions);
  const allTypes = [
    ...defaultTypes,
    ...additionalTypesOptions
      .filter((type) => !defaultTypes.includes(type.label))
      .map((type) => type.label),
  ];
  return (
    <VStack
      position="absolute"
      top="0"
      right="100%"
      bg="white"
      boxShadow="md"
      borderRadius="md"
      p={2}
      zIndex={999}
      minW="220px"
    >
      <VStack align="start" spacing="2px" maxH={500} overflowY="auto" w="100%">
        {allTypes?.map((option) => {
          const isOptionEnabled = additionalTypesOptions.some((opt) => opt.label === option);
          return (
            <HStack
              align="center"
              w="100%"
              padding="8px"
              borderRadius="md"
              cursor={isOptionEnabled ? 'pointer' : 'not-allowed'}
              gap="8px"
              _hover={{ bg: 'bg.hover' }}
              onClick={() => {
                if (isOptionEnabled) {
                  selectedAdditionalTypes?.includes(option)
                    ? setSelectedAdditionalTypes?.(
                        selectedAdditionalTypes.filter((opt) => opt !== option)
                      )
                    : setSelectedAdditionalTypes?.([...(selectedAdditionalTypes ?? []), option]);
                }
              }}
            >
              <Checkbox
                isChecked={selectedAdditionalTypes?.includes(option)}
                isDisabled={!isOptionEnabled}
              />
              <Typography
                variant="bodyStrong"
                color={isOptionEnabled ? 'text.default' : 'text.disabled'}
              >
                {option}
              </Typography>
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

const MaterialityMenu = ({
  materialityFilterOptions,
  selectedMaterialityOptions,
  setSelectedMaterialityOptions,
}: {
  materialityFilterOptions: {
    ref: string;
    label: string;
    disabled: boolean;
  }[];
  selectedMaterialityOptions?: string[];
  setSelectedMaterialityOptions?: (val: string[]) => void;
}) => {
  return (
    <VStack
      position="absolute"
      top="0"
      right="100%"
      bg="white"
      boxShadow="md"
      borderRadius="md"
      p={2}
      zIndex={999}
      minW="250px"
    >
      <VStack align="start" spacing="2px" maxH={500} overflowY="auto" w="100%">
        {materialityFilterOptions?.map((option) => {
          return (
            <HStack
              align="center"
              w="100%"
              padding="8px"
              borderRadius="md"
              cursor={!option.disabled ? 'pointer' : 'not-allowed'}
              gap="8px"
              _hover={{ bg: 'bg.hover' }}
              onClick={() => {
                if (!option.disabled) {
                  selectedMaterialityOptions?.includes(option.ref)
                    ? setSelectedMaterialityOptions?.(
                        selectedMaterialityOptions.filter((opt) => opt !== option.ref)
                      )
                    : setSelectedMaterialityOptions?.([
                        ...(selectedMaterialityOptions ?? []),
                        option.ref,
                      ]);
                }
              }}
            >
              <Checkbox
                isChecked={selectedMaterialityOptions?.includes(option.ref)}
                isDisabled={option.disabled}
              />
              <Typography
                variant="bodyStrong"
                color={!option.disabled ? 'text.default' : 'text.disabled'}
                noOfLines={1}
              >
                {option.label}
              </Typography>
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

const MembersMenu = ({
  search,
  setSearch,
  members,
  selectedUserId,
  taskFilterOption,
  setSelectedUserId,
  setTaskFilter,
}: {
  search: string;
  setSearch: (s: string) => void;
  members: TeamMember[];
  selectedUserId: string;
  taskFilterOption: TaskFilterOptions;
  setSelectedUserId: (val: string) => void;
  setTaskFilter: (val: TaskFilterOptions) => void;
}) => {
  return (
    <VStack
      position="absolute"
      top="0"
      right="100%"
      bg="white"
      boxShadow="md"
      borderRadius="md"
      p={2}
      zIndex={999}
      minW="220px"
    >
      <SearchInput
        search={search}
        setSearch={setSearch}
        placeholder="Search"
        size="sm"
        onClick={(e) => e.stopPropagation()}
      />
      <VStack align="start" spacing={1} maxH={500} overflowY="auto" w="100%">
        {members
          .filter((user) => user.name?.toLowerCase().includes(search.toLowerCase()))
          .map((member) => {
            const isSelected =
              member.id === selectedUserId && taskFilterOption === TaskFilterOptions.members;
            return (
              <HStack
                key={member.id}
                justify="space-between"
                align="center"
                w="100%"
                padding="8px"
                borderRadius="md"
                cursor="pointer"
                _hover={{ bg: 'bg.hover' }}
                onClick={() => {
                  setTaskFilter(TaskFilterOptions.members);
                  setSelectedUserId(member.id ?? '');
                }}
              >
                <Typography variant="bodyStrong" color={isSelected ? 'text.selected' : undefined}>
                  {member.name}
                </Typography>
                {isSelected && <CheckIcon color="text.selected" boxSize={3} />}
              </HStack>
            );
          })}
      </VStack>
    </VStack>
  );
};

export const TaskFilter = ({
  taskFilterOption,
  selectedUserId,
  setSelectedUserId,
  setTaskFilter,
  withLeftIcon,
  selectedAdditionalTypes,
  setSelectedAdditionalTypes,
  selectedMaterialityOptions,
  setSelectedMaterialityOptions,
  additionalTypesOptions,
  materialityFilterOptions,
}: {
  taskFilterOption: TaskFilterOptions;
  selectedUserId: string;
  setSelectedUserId: (val: string) => void;
  setTaskFilter: (val: TaskFilterOptions) => void;
  withLeftIcon?: boolean;
  selectedAdditionalTypes?: string[];
  setSelectedAdditionalTypes?: (val: string[]) => void;
  selectedMaterialityOptions?: string[];
  setSelectedMaterialityOptions?: (val: string[]) => void;
  additionalTypesOptions?: {
    label: string;
    ref: string;
  }[];
  materialityFilterOptions: {
    ref: string;
    label: string;
    disabled: boolean;
  }[];
}) => {
  const [subMenu, setSubMenu] = useState<string>('');
  const [search, setSearch] = useState('');
  const members = useTeamMembers(TeamMembersFilterOption.Active);

  const optionsWithSubMenus = [
    TaskFilterOptions.members,
    TaskFilterOptions.additionalInformation,
    TaskFilterOptions.materiality,
  ];

  return (
    <Menu
      closeOnSelect={
        subMenu !== TaskFilterOptions.additionalInformation &&
        subMenu !== TaskFilterOptions.materiality
      }
    >
      <MenuButton
        as={Button}
        size="sm"
        variant="ghost"
        leftIcon={withLeftIcon ? <FilterIcon /> : undefined}
        rightIcon={!withLeftIcon ? <FilterIcon /> : undefined}
      >
        {taskFilterOption === TaskFilterOptions.members
          ? `${members.members.find((user) => user?.id === selectedUserId)?.name}'s tasks`
          : taskFilterOption}
      </MenuButton>
      <MenuList minW="200px" padding="6px" w="250px">
        <HStack px="8px" py="6px" w="100%" justifyContent="space-between">
          <Typography variant="detail" color="text.muted">
            Filter
          </Typography>
          {(taskFilterOption !== TaskFilterOptions.allTasks ||
            !!selectedAdditionalTypes?.length ||
            !!selectedMaterialityOptions?.length) && (
            <Typography
              variant="detail"
              color="text.muted"
              cursor="pointer"
              onClick={() => {
                setTaskFilter(TaskFilterOptions.allTasks);
                setSelectedAdditionalTypes?.([]);
                setSelectedMaterialityOptions?.([]);
              }}
            >
              Clear all
            </Typography>
          )}
        </HStack>
        {Object.values(TaskFilterOptions)
          ?.filter((opt) =>
            !selectedAdditionalTypes
              ? opt !== TaskFilterOptions.additionalInformation &&
                opt !== TaskFilterOptions.markedAsIrrelevant
              : true
          )
          .filter((opt) =>
            !selectedMaterialityOptions ? opt !== TaskFilterOptions.materiality : true
          )
          .map((label) => (
            <>
              {label === TaskFilterOptions.additionalInformation && (
                <MenuDivider ml="-6px" mr="-6px" my="6px" />
              )}
              <MenuItem
                key={label}
                onMouseEnter={() => setSubMenu(label)}
                position="relative"
                _hover={{ bg: 'bg.selected' }}
                onClick={() => (!optionsWithSubMenus.includes(label) ? setTaskFilter(label) : {})}
                onMouseLeave={() => setSubMenu('')}
              >
                <HStack justify="space-between" align="center" w="100%" gap="12px">
                  <Typography
                    variant="bodyStrong"
                    color={label === taskFilterOption ? 'text.selected' : undefined}
                  >
                    {label}
                  </Typography>
                  {(label === TaskFilterOptions.members ||
                    label === TaskFilterOptions.additionalInformation ||
                    label === TaskFilterOptions.materiality) && <ChevronRightIcon />}
                  {label === taskFilterOption && !optionsWithSubMenus.includes(label) && (
                    <CheckIcon color="text.selected" boxSize={3} />
                  )}
                </HStack>
                {subMenu === TaskFilterOptions.additionalInformation &&
                  label === TaskFilterOptions.additionalInformation && (
                    <AdditionalTypesMenu
                      additionalTypesOptions={additionalTypesOptions!}
                      selectedAdditionalTypes={selectedAdditionalTypes}
                      setSelectedAdditionalTypes={setSelectedAdditionalTypes}
                    />
                  )}

                {subMenu === TaskFilterOptions.materiality &&
                  label === TaskFilterOptions.materiality && (
                    <MaterialityMenu
                      materialityFilterOptions={materialityFilterOptions!}
                      selectedMaterialityOptions={selectedMaterialityOptions}
                      setSelectedMaterialityOptions={setSelectedMaterialityOptions}
                    />
                  )}

                {subMenu === TaskFilterOptions.members && label === TaskFilterOptions.members && (
                  <MembersMenu
                    members={members.members}
                    search={search}
                    setSearch={setSearch}
                    selectedUserId={selectedUserId}
                    setSelectedUserId={setSelectedUserId}
                    setTaskFilter={setTaskFilter}
                    taskFilterOption={taskFilterOption}
                  />
                )}
              </MenuItem>
            </>
          ))}
      </MenuList>
    </Menu>
  );
};

export const GroupOrderSelector = ({
  groupOrOrder,
  setGroupOrOrder,
}: {
  groupOrOrder: GroupOrderOptions;
  setGroupOrOrder: (val: GroupOrderOptions) => void;
}) => {
  return (
    <Select
      isSearchable={false}
      ghostVariant
      size="sm"
      width="fit-content"
      menuWidth="150px"
      defaultValue={{ label: groupOrOrder, value: groupOrOrder }}
      onChange={(val) => setGroupOrOrder(val?.value ?? GroupOrderOptions.byType)}
      options={[
        {
          label: GroupOrderOptions.byType,
          value: GroupOrderOptions.byType,
        },
        {
          label: GroupOrderOptions.byFrameworkOrder,
          value: GroupOrderOptions.byFrameworkOrder,
        },
      ]}
    />
  );
};

export const QuarterSelector = ({
  defaultQuarter,
  setSelectedQuarter,
}: {
  defaultQuarter: TimePeriodsEnums;
  setSelectedQuarter: (val: TimePeriodsEnums) => void;
}) => {
  return (
    <Select
      isSearchable={false}
      ghostVariant
      size="sm"
      width="fit-content"
      menuWidth="110px"
      onChange={(val) => setSelectedQuarter(val?.value ?? TimePeriodsEnums.year)}
      defaultValue={{
        label: defaultQuarter === TimePeriodsEnums.year ? 'Full year' : defaultQuarter,
        value: defaultQuarter,
      }}
      options={[
        {
          label: 'Full year' as TimePeriodsEnums,
          value: TimePeriodsEnums.year,
        },
        {
          label: TimePeriodsEnums.q1,
          value: TimePeriodsEnums.q1,
        },
        {
          label: TimePeriodsEnums.q2,
          value: TimePeriodsEnums.q2,
        },
        {
          label: TimePeriodsEnums.q3,
          value: TimePeriodsEnums.q3,
        },
        {
          label: TimePeriodsEnums.q4,
          value: TimePeriodsEnums.q4,
        },
      ]}
    />
  );
};

export const CommentsCounter = ({
  row,
  selectedQuarter,
  companyReportingUnit,
  onClick,
}: {
  row: AggregatedMetricsTableData;
  selectedQuarter: string;
  companyReportingUnit?: string;
  onClick: () => void;
}) => {
  const { answer } = useGetDatapointValues(row, companyReportingUnit);
  const { notifications } = useNotifications();

  const dataPoint = useMemo(
    () => answer?.datapoints.find((dp) => dp.timeframe === selectedQuarter),
    [answer, selectedQuarter]
  );

  const commentsCount = useMemo(
    () => dataPoint?.thread?.comments_aggregate.aggregate?.count ?? 0,
    [dataPoint]
  );

  const hasUnread = useMemo(
    () =>
      notifications.some(
        (notf) => notf.comment.thread.esrsDatapoint?.id === dataPoint?.id && !notf.isRead
      ),
    [notifications, dataPoint]
  );

  return (
    <Button
      size="sm"
      variant="ghost"
      leftIcon={<CommentIcon color={hasUnread ? 'text.action' : 'text.muted'} />}
      onClick={onClick}
    >
      <Typography
        variant="bodyStrong"
        color={hasUnread ? 'text.action' : 'text.muted'}
        opacity={commentsCount === 0 ? 0.2 : 1}
      >
        {commentsCount}
      </Typography>
    </Button>
  );
};
const calculateDataCollection = ({ metricRow }: { metricRow: AggregatedMetricsTableData }) => {
  const materialMetric = metricRow.metric.materialMetrics?.[0];
  if (!metricRow.subRows?.length) {
    return materialMetric?.dataCollection;
  }
  const dataCollectionOfChildren = new Set();
  for (const subRow of metricRow.subRows) {
    dataCollectionOfChildren.add(calculateDataCollection({ metricRow: subRow }));
  }
  if (dataCollectionOfChildren.size === 1) {
    return dataCollectionOfChildren.values().next().value;
  }
  return '';
};

export const usePrepareMetricIcon = ({
  row,
  isGroup,
  metricRow,
  isBusinessUnit,
}: {
  row?: Row<TableData<MetricsTableData>>;
  isGroup: boolean;
  metricRow: AggregatedMetricsTableData;
  isBusinessUnit?: boolean;
}) => {
  const { reportingUnitId } = useParams();
  const subMetrics = useMemo(() => {
    return row?.subRows.map((subRow) => ({
      name: getRowName(subRow.original) ?? '',
      reference: subRow.original.metric.reference,
    }));
  }, [row]);
  const materialMetric = useMemo(() => metricRow.metric.materialMetrics?.[0], [metricRow]);

  const showInput = useMemo(
    () =>
      (!metricRow.subRows?.length &&
        ((materialMetric?.dataCollection === DataCollectionLevel.group && isGroup) ||
          (materialMetric?.dataCollection === DataCollectionLevel.company && !isBusinessUnit))) ||
      (isFullMetricOnBULevel(metricRow.metric) && isBusinessUnit),
    [metricRow, materialMetric, isBusinessUnit, isGroup]
  );
  const type = useMemo(
    () =>
      calculateType({
        row,
        showInput,
      }),
    [row, showInput]
  );
  const dataCollection = useMemo(() => calculateDataCollection({ metricRow }), [metricRow]);
  return {
    type,
    calculation: row?.original.metric.calculation,
    subMetrics,
    isBusinessUnits: dataCollection === DataCollectionLevel.reportingUnits && !reportingUnitId,
    isSubsidiaries: dataCollection === DataCollectionLevel.subsidiaries,
    isQuarterly: materialMetric?.frequency === FrequencyEnums.quarterly,
  };
};
